import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import { ExpandMore, ExpandLess as ExpandLessIcon, Close as CloseIcon } from "@mui/icons-material";
import usePermissions from "../hooks/usePermissions";
import { PERMISSIONS } from "../constants/permissions";
import { useFeatures } from "../hooks/usePermissions";
import { PAGE_FEATURE } from "../utils/features";

const submenusConfig = {
  reports: ["/standards", "/csrd", "/brsr", "/Materiality", "/ReportingModules", "/GenerateReport", "/PreviewReport", "/internal-reports"],
  company: ["/listofentities", "/company", "/listofbusinessunits", "/Businessunit", "/listsitelocations", "/sitelocation", "/repository"],
  settings: ["/listofusers", "/adduser", "/organizations"],
};

const includesAnyPath = (pathname, paths) => paths.some((path) => pathname.includes(path));

const SideNavigation = ({ isSidebarOpen, toggleSidebar }) => {
  const location = useLocation();
  const dashboardPaths = [
    "/esg-dashboard",
    "/scope1",
    "/scope2",
    "/Employee-commute-dashboard",
    "/Business-travel-dashboard",
    "/Transportation-Distribution-Dashboard",
    "/transportation-Distribution-Dashboard",
    "/waste-dashboard",
    "/Travel-Logistic-Dashboard",
    "/brsr/dashboard",
    "/performance-dashboard",
  ];
  const comingSoonSource = new URLSearchParams(location.search).get("source");
  const isSettingsComingSoon = location.pathname === "/comingsoon" && comingSoonSource === "settings";
  const isDashboardComingSoon = location.pathname === "/comingsoon" && comingSoonSource === "dashboard";
  const isInsightsComingSoon = location.pathname === "/comingsoon" && comingSoonSource === "insights";
  // Note: /advisor is NOT here — the Advisor has its own sidebar item, and
  // listing it under insightsPaths made both items highlight at once.
  const insightsPaths = ["/insights", "/assessing-low-re", "/electricity-cost-evaluation", "/assessing-elec-usage"];
  const isInsightsActive = insightsPaths.includes(location.pathname) || isInsightsComingSoon;
  const isDashboardActive = dashboardPaths.includes(location.pathname) || isDashboardComingSoon;
  const isGreenButtonActive =
    location.pathname === "/comingsoon" &&
    !isSettingsComingSoon &&
    !isDashboardComingSoon &&
    !isInsightsComingSoon;
  const isSettingsActive = includesAnyPath(location.pathname, submenusConfig.settings) || isSettingsComingSoon;
  const [open, setOpen] = useState(true);
  const { reportsMenu, companyMenu, settingsMenu } = Object.entries(submenusConfig).reduce((acc, [key, value]) => {
    acc[`${key}Menu`] = includesAnyPath(location.pathname, value) ? "showMenu" : "";
    return acc;
  }, {});
  const userEmail = localStorage.getItem("userName");
  // Which modules the organization's plan includes. Pages not mapped in
  // PAGE_FEATURE have no feature requirement. While loading (or for staff),
  // everything reads as allowed — the proxy enforces the real answer.
  const { has: hasFeatureKey } = useFeatures();
  const canAccessPage = (page) => {
    const key = PAGE_FEATURE[page];
    return key ? hasFeatureKey(key) : true;
  };
  const { has: hasPermission, loading: permsLoading } = usePermissions();
  // Until perms resolve, don't paint items as disabled — that flashed the admin
  // sections greyed on a cold load. Enforcement is server-side; this is only UX.
  const canManageUsers = permsLoading || hasPermission(PERMISSIONS.MANAGE_USERS_ROLES);
  const canManageOrg = permsLoading || hasPermission(PERMISSIONS.MANAGE_ORG_STRUCTURE);
  const canManageConfig = permsLoading || hasPermission(PERMISSIONS.MANAGE_CONFIGURATION);
  // EcoPrism staff: Organizations lives under Settings and is the only item a
  // staff account without company roles can use, so it must keep Settings open.
  const isStaff = hasPermission(PERMISSIONS.PLATFORM_STAFF);
  // Function to toggle submenu
  const toggleSubMenu = (event) => {
    event.preventDefault();
    const listItem = event.currentTarget.parentElement;
    const allListItems = document.querySelectorAll(".nav-links > li");

    // Close all other dropdowns
    allListItems.forEach((item) => {
      if (item !== listItem && item.classList.contains("showMenu")) {
        item.classList.remove("showMenu");
      }
    });
    // Toggle the clicked dropdown
    listItem.classList.toggle("showMenu");
  };

  const isAllSubmenuDisabledCompany = (!canAccessPage("listofentities") && !canAccessPage("listofbusinessunits") && !canAccessPage("listsitelocations") && !canAccessPage("repository")) || !canManageOrg;

  const isAllSubmenuDisabledSetting = ((!canAccessPage("listofusers") && !canAccessPage("adduser")) || !canManageUsers) && !isStaff;

  useEffect(() => {
    const storedSidebarOpen = JSON.parse(localStorage.getItem("sidebarOpen"));
    if (storedSidebarOpen !== null) {
      setOpen(storedSidebarOpen);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebarOpen", JSON.stringify(isSidebarOpen));
    setOpen(isSidebarOpen);
  }, [isSidebarOpen]);

  return (
    <div className={`sidebar ${isSidebarOpen ? "open" : "close"}`}>
      {/* Sidebar content */}
      <div className={`logo-container ${isSidebarOpen ? "logo-sidebar-expanded" : "logo-sidebar-collapsed"}`}>
        <Link to="/home">
          <img src={isSidebarOpen ? "/assets/img/logo.png" : "/assets/img/favicon-light.png"} alt="ecoPRISM" />
        </Link>
        {isSidebarOpen && (
          <IconButton
            className="close-icon"
            onClick={toggleSidebar}
            style={{
              position: "absolute",
              right: "10px",
              top: "10px",
              color: "white",
              opacity: "0.7",
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </div>

      <ul className="nav-links">
        {/* Navigation links */}
        <li>
          <Link to={canAccessPage("esg-dashboard") ? "/esg-dashboard" : "#"} className={`${isDashboardActive ? (isSidebarOpen ? "icon-list-active" : "icon-list-collapse-active") : `main-menu-item ${canAccessPage("esg-dashboard") ? "" : "disabled-link"}`}`}>
            <div className={isSidebarOpen ? "list-icon" : "list-icon-collapse"}>
              <img src={isDashboardActive ? "/assets/img/sidebar/active/Dashboard.png" : "/assets/img/sidebar/Dashboard.png"} alt="Dashboard" className="sidebar-image" />
            </div>
            <span className="link_name">Dashboard</span>
          </Link>
          <ul className="sub-menu blank">
            <li>
              <Link className="link_name" to={canAccessPage("esg-dashboard") ? "/esg-dashboard" : "#"}>
                Dashboard
              </Link>
            </li>
          </ul>
        </li>
        {/* Reports */}
        <li className={`reports-dropdown ${reportsMenu}`}>
          <div className="iocn-link" onClick={toggleSubMenu}>
            <Link to="#">
              <div className={isSidebarOpen ? "list-icon" : "list-icon-collapse"}>
                <img src={includesAnyPath(location.pathname, submenusConfig.reports) ? "/assets/img/sidebar/active/Reports.png" : "/assets/img/sidebar/Reports.png"} alt="Reports" className="sidebar-image" />
              </div>
              <span className="link_name">Reports</span>
            </Link>
            <ExpandMore className="chevron-down" />
          </div>

          <ul className="reports sub-menu submenu-list">
            <li className="submenu-header">
              <Link to="#" className="link_name">
                Reports
              </Link>
              <ExpandLessIcon className="chevron-up" />
            </li>
            <li>
              <Link className={location.pathname === "/internal-reports" ? "icon-list-active-submenu submenu-item" : `submenu-item main-menu-item ${canAccessPage("internal-reports") ? "" : "disabled-link"}`} to={canAccessPage("internal-reports") ? "/internal-reports" : "#"}>
                Internal
              </Link>
            </li>
            <li>
              <Link className={location.pathname === "/standards" || location.pathname === "/csrd" || location.pathname.includes("/brsr") || location.pathname === "/Materiality" || location.pathname === "/ReportingModules" || location.pathname === "/GenerateReport" || location.pathname === "/PreviewReport" ? "icon-list-active-submenu submenu-item" : `submenu-item main-menu-item ${canAccessPage("/standards") ? "" : "disabled-link"}`} to={canAccessPage("/standards") ? "/standards" : "#"}>
                Standards & Frameworks
              </Link>
            </li>
          </ul>
        </li>

        {/* Green Button */}
        <li>
          <Link to={canAccessPage("comingsoon") ? "/comingsoon" : "#"} className={`${isGreenButtonActive ? (isSidebarOpen ? "icon-list-active" : "icon-list-collapse-active") : `main-menu-item ${canAccessPage("comingsoon") ? "" : "disabled-link"}`}`}>
            <div className={isSidebarOpen ? "list-icon" : "list-icon-collapse"}>
              <img src={isGreenButtonActive ? "/assets/img/sidebar/active/Greenbutton.png" : "/assets/img/sidebar/Green.png"} alt="Green Button" className="sidebar-image" />
            </div>
            <span className="link_name">Green Button</span>
          </Link>

          <ul className="sub-menu blank">
            <li>
              <Link className="link_name " to={canAccessPage("comingsoon") ? "/comingsoon" : "#"}>
                Green Button
              </Link>
            </li>
          </ul>
        </li>
        {/* Forms & Apps */}
        <li>
          <Link
            to={canAccessPage("forms-apps") ? "/forms-apps" : "#"}
            className={`${location.pathname === "/forms-apps" || location.pathname === "/employee-commute-data" || location.pathname === "/start-new-survey" || location.pathname === "/employee-commute" || location.pathname === "/coming-soon" || location.pathname === "/coming-soon-eol" || location.pathname === "/coming-soon-logistics" || location.pathname === "/coming-soon-packaging" ? (isSidebarOpen ? "icon-list-active" : "icon-list-collapse-active") : `main-menu-item ${canAccessPage("forms-apps") ? "" : "disabled-link"}`}`}
          >
            <div className={isSidebarOpen ? "list-icon" : "list-icon-collapse"}>
              <img
                src={location.pathname === "/forms-apps" || location.pathname === "/employee-commute-data" || location.pathname === "/start-new-survey" || location.pathname === "/employee-commute" || location.pathname === "/coming-soon" || location.pathname === "/coming-soon-eol" || location.pathname === "/coming-soon-logistics" || location.pathname === "/coming-soon-packaging" ? "/assets/img/sidebar/active/Formsapps.png" : "/assets/img/sidebar/Forms&apps.png"}
                alt="Forms & Apps"
                className="sidebar-image"
              />
            </div>
            <span className="link_name">Forms & Apps</span>
          </Link>

          <ul className="sub-menu blank">
            <li>
              <Link className="link_name" to={canAccessPage("forms-apps") ? "/forms-apps" : "#"}>
                Forms & Apps
              </Link>
            </li>
          </ul>
        </li>
        {/* Insights */}
        <li>
          <Link to={canAccessPage("insights") ? "/insights" : "#"} className={`${isInsightsActive ? (isSidebarOpen ? "icon-list-active" : "icon-list-collapse-active") : `main-menu-item ${canAccessPage("insights") ? "" : "disabled-link"}`}`}>
            <div className={isSidebarOpen ? "list-icon" : "list-icon-collapse"}>
              <img src={isInsightsActive ? "/assets/img/sidebar/active/Insights.png" : "/assets/img/sidebar/Insights.png"} alt="Insights" className="sidebar-image" />
            </div>
            <span className="link_name">Insights</span>
          </Link>

          <ul className="sub-menu blank">
            <li>
              <Link className="link_name" to={canAccessPage("insights") ? "/insights" : "#"}>
                Insights
              </Link>
            </li>
          </ul>
        </li>
        {/* Advisor (shares the insights feature gate). Entra sessions only: the
            Advisor's server guard requires a verified Microsoft sign-in, so for
            password sessions the item is not shown at all rather than leading
            to a dead end. */}
        {localStorage.getItem("loginType") === "AAD" && (
        <li>
          <Link to={canAccessPage("insights") ? "/advisor" : "#"} className={`${location.pathname === "/advisor" ? (isSidebarOpen ? "icon-list-active" : "icon-list-collapse-active") : `main-menu-item ${canAccessPage("insights") ? "" : "disabled-link"}`}`}>
            <div className={isSidebarOpen ? "list-icon" : "list-icon-collapse"}>
              <img src={location.pathname === "/advisor" ? "/assets/img/sidebar/active/Insights.png" : "/assets/img/sidebar/Insights.png"} alt="Advisor" className="sidebar-image" />
            </div>
            <span className="link_name">Advisor</span>
          </Link>

          <ul className="sub-menu blank">
            <li>
              <Link className="link_name" to={canAccessPage("insights") ? "/advisor" : "#"}>
                Advisor
              </Link>
            </li>
          </ul>
        </li>
        )}
        {/* Configuration — MANAGE_CONFIGURATION (disabled when lacking permission) */}
        <li style={{ pointerEvents: canManageConfig ? "auto" : "none", opacity: canManageConfig ? 1 : 0.5 }}>
          <Link to={canAccessPage("allconfiguration") ? "/allconfiguration" : "#"} className={`${location.pathname === "/allconfiguration" || location.pathname === "/configuration" || location.pathname === "/reporthistory" ? (isSidebarOpen ? "icon-list-active" : "icon-list-collapse-active") : "main-menu-item"} ${canAccessPage("allconfiguration") ? "" : "disabled-link"}`}>
            <div className={isSidebarOpen ? "list-icon" : "list-icon-collapse"}>
              <img src={location.pathname === "/allconfiguration" || location.pathname === "/configuration" || location.pathname === "/reporthistory" ? "/assets/img/sidebar/active/Configuration.png" : "/assets/img/sidebar/Configuration.png"} alt="Configuration" className="sidebar-image" />
            </div>
            <span className="link_name">Configuration</span>
          </Link>
          <ul className="sub-menu blank">
            <li>
              <Link className="link_name" to={canAccessPage("allconfiguration") ? "/allconfiguration" : "#"}>
                Configuration
              </Link>
            </li>
            <li>
              <Link className="link_name" to={canManageConfig ? "/waste-supplier-factors" : "#"}>
                Waste supplier factors
              </Link>
            </li>
          </ul>
        </li>
        {/* Company — MANAGE_ORG_STRUCTURE (disabled via isAllSubmenuDisabledCompany) */}
        <li className={`company-dropdown ${companyMenu} ${isAllSubmenuDisabledCompany ? "disabled-dropdown" : ""}`}>
          <div
            className="iocn-link"
            onClick={(e) => {
              if (!isAllSubmenuDisabledCompany) {
                toggleSubMenu(e);
              } else {
                e.preventDefault();
              }
            }}
            style={{
              pointerEvents: isAllSubmenuDisabledCompany ? "none" : "auto",
              opacity: isAllSubmenuDisabledCompany ? 0.5 : 1,
            }}
          >
            <Link to="#">
              <div className={isSidebarOpen ? "list-icon" : "list-icon-collapse"}>
                <img src={includesAnyPath(location.pathname, submenusConfig.company) ? "/assets/img/sidebar/active/Company.png" : "/assets/img/sidebar/Company.png"} alt="Company" className="sidebar-image" />
              </div>
              <span className="link_name">Company</span>
            </Link>
            <ExpandMore className="chevron-down" />
          </div>
          <ul className="company sub-menu submenu-list">
            <li className="submenu-header">
              <Link className="link_name" to="#">
                Company
              </Link>
              <ExpandLessIcon className="chevron-up" />
            </li>
            <li>
              <Link className={location.pathname === "/listofentities" || location.pathname === "/company" ? "icon-list-active-submenu submenu-item" : `submenu-item main-menu-item ${canAccessPage("listofentities") ? "" : "disabled-link"}`} to={canAccessPage("listofentities") ? "/listofentities" : "#"}>
                Legal Entities
              </Link>
            </li>
            <li>
              <Link to={canAccessPage("listofbusinessunits") ? "/listofbusinessunits" : "#"} className={location.pathname === "/listofbusinessunits" || location.pathname === "/Businessunit" ? "icon-list-active-submenu submenu-item" : `submenu-item main-menu-item ${canAccessPage("listofbusinessunits") ? "" : "disabled-link"}`}>
                Business Units
              </Link>
            </li>
            <li>
              <Link to={canAccessPage("listsitelocations") ? "/listsitelocations" : "#"} className={location.pathname === "/listsitelocations" || location.pathname === "/sitelocation" ? "icon-list-active-submenu submenu-item" : `submenu-item main-menu-item ${canAccessPage("listsitelocations") ? "" : "disabled-link"}`}>
                Site Locations
              </Link>
            </li>
            {/* <li>
              <a
                href="/repository"
                className={
                  location.pathname === "/repository"
                    ? "icon-list-active-submenu submenu-item"
                    : "submenu-item main-menu-item"
                }
              >
                Repository
              </a>
            </li> */}
          </ul>
        </li>
        {/* Settings — MANAGE_USERS_ROLES (disabled via isAllSubmenuDisabledSetting) */}
        <li className={`settings-dropdown ${settingsMenu}`}>
          <div
            className="iocn-link"
            onClick={(e) => {
              if (!isAllSubmenuDisabledSetting) {
                toggleSubMenu(e);
              } else {
                e.preventDefault();
              }
            }}
            style={{
              pointerEvents: isAllSubmenuDisabledSetting ? "none" : "auto",
              opacity: isAllSubmenuDisabledSetting ? 0.5 : 1,
            }}
          >
            <Link to="#">
              <div className={isSidebarOpen ? "list-icon" : "list-icon-collapse"}>
                <img src={isSettingsActive ? "/assets/img/sidebar/active/Settings.png" : "/assets/img/sidebar/Settings.png"} alt="Settings" className="sidebar-image" />
              </div>
              <span className="link_name">Settings</span>
            </Link>
            <ExpandMore className="chevron-down" />
          </div>
          <ul className="settings sub-menu submenu-list">
            <li className="submenu-header">
              <Link to="" className="link_name">
                Settings
              </Link>
              <ExpandLessIcon className="chevron-up" />
            </li>
            <li>
              <Link className={location.pathname === "/listofusers" || location.pathname === "/adduser" || isSettingsComingSoon ? "icon-list-active-submenu submenu-item" : `submenu-item main-menu-item active ${canAccessPage("listofusers") ? "" : "disabled-link"}`} to={canAccessPage("listofusers") ? "/listofusers" : "#"}>
                User Management
              </Link>
            </li>
            {isStaff && (
              <li>
                <Link
                  className={location.pathname.startsWith("/organizations") ? "icon-list-active-submenu submenu-item" : "submenu-item main-menu-item active"}
                  to="/organizations"
                >
                  Organizations
                </Link>
              </li>
            )}
            {isStaff && (
              <li>
                <Link
                  className={location.pathname.startsWith("/waste-factors") ? "icon-list-active-submenu submenu-item" : "submenu-item main-menu-item a"}
                  to="/waste-factors"
                >
                  Waste factor library
                </Link>
              </li>
            )}
          </ul>
        </li>
      </ul>
      {/* Profile section */}
      <div className="profile-details">
        <div className="job">Powered by ecoPRISM</div>
      </div>
    </div>
  );
};

export default SideNavigation;
